import React, { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Modal from 'utils/packages/Modal';
import * as StoreReview from 'utils/packages/storeReview';
import ModalRating from 'features/review/ModalRating';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { selectSessionCount } from 'reducers/selectors';
import { SET_LAST_REVIEW_SESSION_COUNT } from 'actions/types/deviceSettings';
import { logQuest } from 'features/quests/actions';
import { DEFAULT_DELAY_TIME } from 'utils/delay';
import { openMarketplaceReview } from 'utils/openMarketplace';
import Sentry from 'utils/sentry';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { TimestampStorage, TimestampStorageKeys } from 'utils/TimestampStorage';
import { HAS_RATED_VIA_ALERT, HAS_REVIEWED_ON_MARKETPLACE, } from '../../constants';
import { selectEnablePromptMarketplaceReview } from './selectors';
const strings = {
    reviewTitleEnjoying: 'Enjoying the app?',
    reviewTitle: Platform.OS === 'ios'
        ? 'Give us a 5-star review on the App Store!'
        : 'Give us a 5-star review on Google Play!',
    reviewNegative: 'Maybe later',
    reviewPositive: 'Yes, let’s do it!',
};
function showRequestInAppReviewFallback(version) {
    const [title, subtitle] = version === 'ask'
        ? [strings.reviewTitleEnjoying, strings.reviewTitle]
        : [strings.reviewTitle, undefined];
    Modal.showConfirmation({
        title,
        subtitle,
        cancelButtonTitle: strings.reviewPositive,
        onPressCancel: openMarketplaceReview,
        confirmButtonTitle: strings.reviewNegative,
    });
}
async function markAsRatedViaAlert() {
    try {
        await AsyncStorage.setItem(HAS_RATED_VIA_ALERT, 'true');
        await TimestampStorage.stamp(TimestampStorageKeys.RATING_ALERT_PROMPT);
    }
    catch {
        //
    }
}
async function markAsReviewedOnMarketplace() {
    try {
        await AsyncStorage.setItem(HAS_REVIEWED_ON_MARKETPLACE, 'true');
        await TimestampStorage.stamp(TimestampStorageKeys.MARKETPLACE_REVIEW_PROMPT);
    }
    catch {
        //
    }
}
const useModalRating = () => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const navigation = useMainStackNavigation();
    const setLastReviewSessionCount = useCallback(() => {
        dispatch({
            type: SET_LAST_REVIEW_SESSION_COUNT,
            lastReviewSessionCount: selectSessionCount(store.getState()),
        });
    }, []);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => clearTimeout(timeoutRef.current), []);
    const requestInAppReview = useCallback(async (fallbackVersion) => {
        dispatch(logQuest('RATE'));
        markAsRatedViaAlert();
        try {
            if (await StoreReview.isAvailableAsync()) {
                await StoreReview.requestReview();
                return;
            }
        }
        catch {
            // noop, we can fallback
        }
        timeoutRef.current = setTimeout(() => {
            showRequestInAppReviewFallback(fallbackVersion);
        }, DEFAULT_DELAY_TIME);
    }, []);
    const requestMarketplaceReview = useCallback(async () => {
        try {
            await openMarketplaceReview();
        }
        catch (err) {
            Sentry.logError({ filename: 'useModalRating', err });
            return;
        }
        await markAsReviewedOnMarketplace();
    }, []);
    const requestReview = useCallback(({ skipLoveCheck, openMarketplace } = {}) => {
        const enablePromptMarketplaceReview = selectEnablePromptMarketplaceReview(store.getState());
        const fallbackVersion = skipLoveCheck ? 'ask' : 'direct'; // don't ask twice
        if (skipLoveCheck) {
            if (openMarketplace && enablePromptMarketplaceReview) {
                requestMarketplaceReview();
            }
            else {
                requestInAppReview(fallbackVersion);
            }
            return;
        }
        setLastReviewSessionCount();
        Modal.show(<ModalRating onPressed={(response) => {
                if (response === 'love') {
                    if (openMarketplace && enablePromptMarketplaceReview) {
                        return 'showLeaveReview';
                    }
                    Modal.hide(() => requestInAppReview(fallbackVersion));
                    return undefined;
                }
                Modal.hide(() => {
                    if (response === 'hate') {
                        navigation.navigate('NegativeFeedback');
                    }
                });
                return undefined;
            }} onLeaveReview={() => {
                Modal.hide(() => requestMarketplaceReview());
            }}/>);
    }, [requestInAppReview, requestMarketplaceReview, setLastReviewSessionCount]);
    return { requestReview };
};
export default useModalRating;
